import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCartStore, cartItemAddOns, cartItemOptionIds } from '@astro/store'
import { api } from '@astro/api'
import { appUrl } from '@astro/config'
import { useAuth } from '@astro/auth'
import { useToast } from '../context/ToastContext.jsx'
import { useCartSync } from '../hooks/useCartSync.js'
import AddressBook, { toList as toAddressList, isDefault as isDefaultAddress } from '../components/sections/AddressBook.jsx'
import PaymentOptions from '../components/sections/PaymentOptions.jsx'
import { getGatewayDriver, gatewayKey } from '../checkout/gateways'
import PaymentProcessingOverlay from '../checkout/PaymentProcessingOverlay.jsx'

const formatINR = (paise) => `₹${Math.round(paise / 100).toLocaleString('en-IN')}`

/** Pull the discount out of the loosely-documented /apply-coupon response:
 * either a percentage or a fixed rupee amount, under a handful of likely keys.
 * Returns { kind: 'percent' | 'fixed', value } or null when unrecognizable. */
function parseCouponDiscount(res) {
  const d = res?.data && typeof res.data === 'object' ? res.data : res ?? {}
  const kind = String(d.discount_type ?? d.type ?? '').toLowerCase().includes('percent')
    ? 'percent'
    : 'fixed'
  const value = parseFloat(
    d.discount_amount ?? d.discount ?? d.coupon_value ?? d.amount ?? d.value ?? NaN
  )
  if (!Number.isFinite(value) || value <= 0) return null
  return { kind, value }
}

/** The human-readable "ORD…" order number — the ONLY key /orders/:orderNumber
 * (and the trackOrder API behind it) resolves by, never the internal numeric
 * order_id. Scans the given responses in priority order, looking through the
 * usual data/order envelopes, and returns the first usable value. Returns null
 * when no source carries one, so the confirmation page falls back to the order
 * history instead of building a dead URL.
 */
function resolveOrderNumber(...sources) {
  for (const src of sources) {
    if (!src || typeof src !== 'object') continue
    for (const candidate of [src.order_number, src.data?.order_number, src.order?.order_number]) {
      if (typeof candidate === 'number' && Number.isFinite(candidate)) return String(candidate)
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
    }
  }
  return null
}

export default function Cart() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { status, user } = useAuth()
  const queryClient = useQueryClient()

  const items = useCartStore((s) => s.items)
  const clear = useCartStore((s) => s.clear)
  const buyNowItem = useCartStore((s) => s.buyNow)
  const setBuyNowQty = useCartStore((s) => s.setBuyNowQty)
  const clearBuyNow = useCartStore((s) => s.clearBuyNow)

  // Express (buy-now) checkout: the hot item rides the store's dedicated
  // buy-now slot and this page charges ONLY that line, leaving the saved
  // basket untouched. Falls back to the regular basket when the slot is
  // empty (order completed, line removed, or a direct /checkout visit).
  const [searchParams] = useSearchParams()
  const express = searchParams.get('mode') === 'buynow' && Boolean(buyNowItem)
  const checkoutItems = express ? [buyNowItem] : items

  const subtotal = checkoutItems.reduce(
    (n, i) => n + (i.price.amount + cartItemAddOns(i)) * i.qty,
    0
  )

  // Two-way local ⇄ server cart sync; these mutators update both sides.
  // The express line never touches the server cart — it gets local mutators.
  const { changeQty, removeItem, serverMeta } = useCartSync()
  const changeLineQty = (item, qty) =>
    express ? setBuyNowQty(qty) : changeQty(item.productId, qty)
  const removeLine = (item) => (express ? clearBuyNow() : removeItem(item.productId))

  const [coupon, setCoupon] = useState('')
  // Validated coupon: { code, kind: 'percent'|'fixed', value } — discount is
  // derived from it each render so it tracks cart changes (percent coupons).
  const [applied, setApplied] = useState(null)
  const [applying, setApplying] = useState(false)

  const applyCoupon = async () => {
    const code = coupon.trim().toUpperCase()
    if (!code) return showToast('Enter a coupon code')
    setApplying(true)
    try {
      const res = await api.cart.applyCoupon({ coupon_code: code })
      const discount = res?.status === false ? null : parseCouponDiscount(res)
      if (!discount) {
        setApplied(null)
        return showToast(res?.message || 'This coupon code is not valid')
      }
      setApplied({ code, ...discount })
      showToast(res?.message || `Coupon "${code}" applied`)
    } catch (err) {
      setApplied(null)
      showToast(err?.message || 'Could not verify the coupon — try again.')
    } finally {
      setApplying(false)
    }
  }
  const [removing, setRemoving] = useState(false)
  const removeCoupon = async () => {
    if (!applied || removing) return
    setRemoving(true)
    try {
      await api.cart.removeCoupon({ coupon_code: applied.code })
      setApplied(null)
      setCoupon('')
      showToast('Coupon removed')
    } catch (err) {
      showToast(err?.message || 'Could not remove the coupon — try again.')
    } finally {
      setRemoving(false)
    }
  }

  // Discount in paise, never exceeding the subtotal.
  const discount = applied
    ? Math.min(
      subtotal,
      applied.kind === 'percent'
        ? Math.round((subtotal * applied.value) / 100)
        : Math.round(applied.value * 100)
    )
    : 0
  const payable = subtotal - discount
  // Effective selections reported up by the accordions (default or clicked).
  const [address, setAddress] = useState(null)
  const [gateway, setGateway] = useState(null)
  const [placing, setPlacing] = useState(false)
  // True from "payment captured" until verify succeeds/fails — drives the
  // full-screen ORDER SYNCHRONIZATION overlay.
  const [verifying, setVerifying] = useState(false)

  // Same query key as AddressBook — shares its cache, no extra request.
  // Lets the page resolve the delivery address directly from the API even
  // if the accordion's onSelect callback hasn't fired.
  const { data: addressData } = useQuery({
    queryKey: ['addresses'],
    queryFn: () => api.addresses.list(),
    enabled: status === 'authenticated',
    retry: false,
  })

  const savedAddresses = toAddressList(addressData)
  // Explicit pick in the AddressBook → API default (is_default: 1/true) → first saved.
  const effectiveAddress =
    address ?? savedAddresses.find(isDefaultAddress) ?? savedAddresses[0] ?? null

  const authorize = async () => {
    if (checkoutItems.length === 0) return showToast('Your basket is empty')
    if (!effectiveAddress) return showToast('Select a delivery address first')
    const method = gatewayKey(gateway)
    if (!method) return showToast('Select a payment option first')
    // Gateways without a registered driver aren't payable yet.
    const driver = getGatewayDriver(gateway)
    if (!driver) {
      return showToast(
        method === 'cod'
          ? 'Cash on Delivery is coming soon — please pay online for now.'
          : 'This payment option is coming soon — please choose Razorpay.'
      )
    }

    // Same place-order contract for both modes — in express mode the payload
    // carries the buy-now line forward instead of the basket.
    // Add-ons ride as option ids so the server charges exactly what this page shows.
    const cartItems = checkoutItems.map((i) => {
      const optionIds = cartItemOptionIds(i)
      return { product_id: Number(i.productId), quantity: i.qty, ...(optionIds.length > 0 && { variation_id: optionIds }) }
    })

    // Order context shared by place-order and the driver's verify call.
    const orderContext = {
      user_id: user?.id ?? '',
      address_id: effectiveAddress.id,
      payment_method: method,
      coupon_code: applied?.code ?? '',
      cart_items: cartItems,
    }

    setPlacing(true)
    try {
      const res = await api.checkout.placeOrder(orderContext)
      if (res?.status === false) {
        return showToast(res?.message || 'Could not create the order — try again.')
      }
      // Upstream may nest the payment bootstrap under `data`/`order` — flatten
      // so gateway drivers see one level of fields regardless of envelope shape.
      const order = {
        ...res,
        ...(res?.data && typeof res.data === 'object' ? res.data : {}),
        ...(res?.order && typeof res.order === 'object' ? res.order : {}),
      }
      await driver.pay({
        order,
        gateway,
        verifyContext: orderContext,
        sessionUser: user,
        onVerifyStart: () => setVerifying(true),
        onSuccess: (verified) => {
          if (express) {
            // Only the hot item was charged — the saved basket must survive.
            clearBuyNow()
          } else {
            // Clear every cart source: the local store AND the cached server
            // cart — a stale ['cart'] cache would let the sync reconciler
            // pull the ordered lines right back into the store.
            clear()
            queryClient.removeQueries({ queryKey: ['cart'] })
          }
          // The order number is assigned when the order row is CREATED
          // (place-order) — its timestamp suffix always matches created_at,
          // well before payment is ever verified — so prefer the value
          // place-order already gave us over whatever the verify response may
          // or may not echo back.
          const orderId = resolveOrderNumber(order, verified)
          navigate('/order-confirmed', { state: { orderId } })
        },
        onFailure: (message) => {
          setVerifying(false)
          showToast(message)
        },
      })
    } catch (err) {
      showToast(err?.message || 'Could not create the order — try again.')
    } finally {
      setPlacing(false)
      setVerifying(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-background font-normal uppercase select-none">
      {verifying && <PaymentProcessingOverlay />}
      <div className="container px-6 mx-auto pt-28 md:pt-32 pb-12">
        {/* Back button */}
        <div className="mb-10">
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="group flex items-center gap-2 text-primary/40 font-normal uppercase tracking-widest text-[10px] hover:text-primary transition-colors"
          >
            <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"></path></svg>
            BACK TO SHOP
          </button>
        </div>

        {checkoutItems.length === 0 ? (
          <div className="bg-white rounded-3xl border border-border/40 shadow-sm py-24 text-center">
            <i className="fa-solid fa-basket-shopping text-4xl text-primary/20"></i>
            <p className="mt-5 text-sm text-primary/50 font-body normal-case">Your basket is empty.</p>
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="mt-6 inline-flex items-center justify-center h-11 px-8 rounded-xl bg-secondary text-white hover:bg-primary transition-all uppercase tracking-widest text-[10px]"
            >
              Explore Divine Products
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* LEFT: Items + Delivery + Trust */}
            <div className="lg:col-span-7 space-y-8">

              {express && (
                <div className="flex items-center gap-3 px-5 py-3 rounded-2xl border border-secondary/30 bg-secondary/5">
                  <i className="fa-solid fa-bolt text-secondary text-xs"></i>
                  <p className="text-[9px] font-normal uppercase tracking-widest text-primary/60">
                    Express checkout — this order is separate from your saved basket
                  </p>
                </div>
              )}

              {/* Item list */}
              <div className="bg-muted/5 rounded-4xl shadow-inner">
                <div className="bg-white rounded-3xl border border-border/10 overflow-hidden shadow-sm">
                  <div className="divide-y divide-border">
                    {checkoutItems.map((item) => {
                      // The server cart line is the source of truth for product
                      // facts; the express line carries its own display facts.
                      const meta = express
                        ? { img: item.image, category: item.category }
                        : serverMeta[item.productId] || {}
                      return (
                        <div key={item.productId} className="p-4 flex flex-col sm:flex-row gap-6 items-center group bg-white hover:bg-muted/5 transition-colors duration-300">
                          <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-border shrink-0 shadow-sm bg-white">
                            <img src={meta.img || '/images/1.webp'} alt={item.title} className="object-cover w-full h-full" />
                          </div>
                          <div className="flex-1 min-w-0 space-y-1 text-center sm:text-left">
                            <p className="text-[9px] font-normal text-secondary uppercase tracking-widest">{meta.category || 'DIVINE'}</p>
                            <h4 className="text-lg font-heading text-primary line-clamp-1 uppercase">{item.title}</h4>
                            {/* Base price + per-unit add-ons (`options`, priced server-side by option id) */}
                            <p className="text-primary font-normal">{formatINR(item.price.amount + cartItemAddOns(item))}</p>
                            {item.options?.length > 0 && (
                              <div className="pt-1 space-y-0.5">
                                {item.options.map((o) => (
                                  <p key={o.key} className="text-[9px] normal-case tracking-wider text-primary/50">
                                    <span className="uppercase text-primary/35">{o.label}:</span> {o.display}
                                    {o.price > 0 && <span className="font-mono text-secondary"> +{formatINR(o.price)}</span>}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-1 border border-border rounded-xl p-1 bg-muted/20">
                              <button onClick={() => changeLineQty(item, item.qty - 1)} className="p-1.5 hover:bg-white rounded-lg text-primary/40 hover:text-primary transition-colors" aria-label="Decrease">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14" /></svg>
                              </button>
                              <span className="w-10 text-center text-sm font-normal text-primary font-mono">{item.qty}</span>
                              <button onClick={() => changeLineQty(item, item.qty + 1)} className="p-1.5 hover:bg-white rounded-lg text-primary/40 hover:text-primary transition-colors" aria-label="Increase">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14" /><path d="M12 5v14" /></svg>
                              </button>
                            </div>
                            <button onClick={() => removeLine(item)} className="p-3 text-danger/40 hover:text-danger hover:bg-danger/5 rounded-xl transition-all" aria-label="Remove">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /><line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" /></svg>
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* Delivery coordinates — accordion address book (saved addresses + add/edit popup) */}
              <AddressBook onSelect={setAddress} />

              {/* Payment options — accordion gateway list (GET /payment-gateways) */}
              <PaymentOptions onSelect={setGateway} />

              {/* Trust cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <TrustCard title="SYSTEM SECURITY" body="your transaction point is processed via an authenticated mission gateway with clinical encryption.">
                  <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" />
                </TrustCard>
                <TrustCard title="DATA PROTECTION" body="spiritual path data and identity points remain private and synchronized under mission protocols.">
                  <rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </TrustCard>
              </div>
            </div>

            {/* RIGHT: Payment sidebar */}
            <aside className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="bg-white rounded-3xl border border-border/40 p-8 md:p-10 space-y-10 shadow-2xl shadow-primary/5">
                {/* Header + promo */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-secondary animate-pulse"></div>
                    <span className="text-[9px] font-normal text-secondary uppercase tracking-widest">ORDER SUMMARY</span>
                  </div>

                  <div className="space-y-4 pt-6 border-t border-dashed border-border/40">
                    <div className="flex items-center gap-2 px-1">
                      <i className="fa-solid fa-tag w-3.5 h-3.5 text-secondary text-xs"></i>
                      <span className="text-[9px] font-normal uppercase tracking-widest text-primary/40">Coupon code</span>
                    </div>
                    {applied ? (
                      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-secondary/30 bg-secondary/5">
                        <div className="flex items-center gap-2 min-w-0">
                          <i className="fa-solid fa-circle-check text-secondary text-xs"></i>
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-primary truncate">
                            {applied.code}
                          </span>
                          <span className="text-[10px] font-mono text-secondary shrink-0">−{formatINR(discount)}</span>
                        </div>
                        <button
                          type="button"
                          onClick={removeCoupon}
                          disabled={removing}
                          className="text-[9px] font-semibold uppercase tracking-widest text-danger/60 hover:text-danger transition-colors shrink-0 disabled:opacity-50"
                        >
                          {removing ? 'REMOVING…' : 'REMOVE'}
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2 p-1 bg-muted/30 rounded-2xl border border-border/40">
                        <input
                          placeholder="Enter coupon code"
                          value={coupon}
                          onChange={(e) => setCoupon(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && !applying && applyCoupon()}
                          className="flex-1 min-w-0 bg-transparent px-4 text-xs font-normal uppercase tracking-widest outline-none placeholder:text-primary/20"
                          type="text"
                        />
                        <button
                          onClick={applyCoupon}
                          disabled={applying}
                          className="inline-flex items-center justify-center whitespace-nowrap shrink-0 active:scale-95 h-10 px-6 rounded-xl bg-primary text-white hover:bg-secondary hover:text-primary transition-all font-normal uppercase tracking-widest text-[9px] shadow-lg shadow-primary/5 disabled:opacity-60 disabled:pointer-events-none"
                        >
                          {applying ? 'Checking…' : 'Apply'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pricing */}
                <div className="space-y-5 pt-8">
                  <div className="flex justify-between items-center text-sm font-body">
                    <span className="text-primary/40 uppercase tracking-widest text-[10px] font-normal">SUBTOTAL</span>
                    <span className="text-primary font-normal">{formatINR(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between items-center text-sm font-body">
                      <span className="text-primary/40 uppercase tracking-widest text-[10px] font-normal">
                        COUPON ({applied.code})
                      </span>
                      <span className="text-secondary font-normal">−{formatINR(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-6 border-t border-border mt-2">
                    <div>
                      <span className="font-heading text-primary text-lg block font-normal uppercase">TOTAL PAYABLE</span>
                      <span className="text-[9px] font-mono font-normal text-primary/20 uppercase tracking-[0.2em]">INCL. OF ALL TAXES</span>
                    </div>
                    <span className="text-4xl font-heading text-primary font-normal">{formatINR(payable)}</span>
                  </div>
                </div>

                {/* Payment gateways */}
                <div className="pt-6 border-t border-dashed border-border/40 space-y-8">
                  <div className="flex items-center gap-4 px-1">
                    <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center text-secondary border border-primary/10">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></svg>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-mono font-normal text-primary/40 uppercase tracking-[0.3em]">Secure.Transaction.Protocol</p>
                      <h4 className="text-sm font-normal text-primary uppercase tracking-widest">Verified Payment Gateway</h4>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Gateway title="UPI system" sub="GPay, PhonePe, Paytm">
                      <rect width="14" height="20" x="5" y="2" rx="2" ry="2" /><path d="M12 18h.01" />
                    </Gateway>
                    <Gateway title="Card Synchronization" sub="Visa, Mastercard, RuPay">
                      <rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" />
                    </Gateway>
                    <Gateway title="Institutional Banking" sub="All Major Indian Banks">
                      <line x1="3" x2="21" y1="22" y2="22" /><line x1="6" x2="6" y1="18" y2="11" /><line x1="10" x2="10" y1="18" y2="11" /><line x1="14" x2="14" y1="18" y2="11" /><line x1="18" x2="18" y1="18" y2="11" /><polygon points="12 2 20 7 4 7" />
                    </Gateway>
                  </div>

                  <div className="space-y-4 pt-4">
                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1 bg-border/40"></div>
                      <span className="text-[8px] font-mono font-normal text-primary/20 uppercase tracking-[0.4em]">Supported.Points</span>
                      <div className="h-px flex-1 bg-border/40"></div>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                      {['Visa', 'Mastercard', 'Maestro', 'RuPay', 'UPI', 'Netbanking'].map((t) => (
                        <span key={t} className="px-3 py-1.5 rounded-lg border border-border/40 bg-muted/30 text-[9px] font-normal text-primary/40 uppercase tracking-widest">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Authorize */}
                <button
                  onClick={authorize}
                  disabled={placing}
                  className="inline-flex items-center justify-center whitespace-nowrap active:scale-95 w-full h-16 rounded-2xl bg-secondary text-white hover:bg-primary transition-all duration-500 font-normal uppercase tracking-[0.3em] text-xs shadow-2xl shadow-secondary/20 group disabled:opacity-60 disabled:pointer-events-none"
                >
                  <div className="flex items-center gap-3">
                    {placing ? (
                      <>PROCESSING PAYMENT<i className="fa-solid fa-spinner animate-spin"></i></>
                    ) : (
                      <>AUTHORIZE TRANSACTION
                        <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                      </>
                    )}
                  </div>
                </button>

                {/* The terms a buyer has to be shown before they pay, linked
                    rather than merely named. The legal pages live on the
                    website app, so these are absolute appUrl links. */}
                <p className="mt-3 text-[11px] leading-relaxed text-primary/45 font-body text-center">
                  By authorizing this payment you agree to our{' '}
                  <a href={appUrl('website', 'terms-of-service')} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">Terms of Service</a>,{' '}
                  <a href={appUrl('website', 'refund-policy')} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">Cancellation &amp; Refund Policy</a>{' '}
                  and{' '}
                  <a href={appUrl('website', 'shipping-policy')} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">Shipping &amp; Delivery Policy</a>.
                </p>
              </div>

              <div className="flex items-center gap-3 px-4 py-3 mt-4 bg-muted/20 rounded-xl border border-border/40">
                <svg className="w-4 h-4 text-primary/20 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
                <p className="text-[9px] text-primary/40 font-body leading-tight uppercase tracking-wider font-normal">ORDERS UNDERGO A 48-HOUR CLINICAL RITUAL SYNCHRONIZATION BEFORE DISPATCH FROM THE MISSION POINT.</p>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  )
}

function TrustCard({ title, body, children }) {
  return (
    <div className="group p-8 bg-white border border-gray-100 rounded-3xl shadow-sm hover:shadow-lg hover:border-secondary/20 transition-all duration-300 space-y-4">
      <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-secondary">{children}</svg>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-primary uppercase tracking-widest">{title}</h4>
        <p className="text-xs text-primary/60 mt-2 leading-relaxed lowercase">{body}</p>
      </div>
    </div>
  )
}

function Gateway({ title, sub, children }) {
  return (
    <div className="p-5 rounded-2xl border border-border/60 bg-white shadow-sm flex flex-col gap-4 group hover:border-secondary transition-all duration-500">
      <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center text-primary/40 group-hover:bg-primary group-hover:text-white transition-all">
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
      </div>
      <div className="space-y-1">
        <p className="text-[10px] font-normal text-primary uppercase tracking-widest">{title}</p>
        <p className="text-[9px] text-primary/40 font-body uppercase tracking-tight font-normal">{sub}</p>
      </div>
    </div>
  )
}
